// ═══════════════════════════════════════════════════════════════════════════
// Vendedores — el panel de administración
//
// Lo que faltaba: la base ya soportaba todo esto y no había por dónde tocarlo.
//
// DOS COSAS QUE LA PANTALLA HACE VISIBLES A PROPÓSITO:
//
//   · Cuántas cuentas asignadas siguen SIN TASA. Con el default en null
//     (migración 0017), asignar no crea comisión — lo cual es seguro, pero
//     deja un pendiente que sin mostrarlo se queda ahí para siempre.
//
//   · El ticket se muestra UNA vez, grande y copiable. No se guarda en ningún
//     lado para volver a verlo: si se pierde, se genera otro. Un código de
//     acceso que queda a la vista en una pantalla compartida es un problema.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import { equipo, invitacionesPendientes, crearInvitacion, revocarInvitacion,
         darDeBaja, fijarEsquema } from "../datos/vendedores.js";
import { puedeMembresia, motivoBloqueo } from "../auth/permisos.js";
import { etiquetaRol } from "../auth/sesion.js";
import { ligaDeTicket, mensajeDeInvitacion } from "../auth/ticket.js";
import { qrComoImagen } from "../dominio/qr.js";
import { esDispositivo } from "../auth/dispositivo.js";
import { CarteraPanel } from "./CarteraPanel.jsx";
import { PagoComision } from "./PagoComision.jsx";
import { fmt } from "../pdf/comunes.js";
import { Card, Label, SectionTitle } from "./basicos.jsx";
import { btnPrimary, btnSecondary, inputStyle, selectStyle } from "./estilos.js";
import { AutorizarPrecios } from "./AutorizarPrecios.jsx";

const ROLES = [
  ["agent",      "Vendedor",     "Su cartera, sus remisiones. No timbra ni cancela."],
  ["manager",    "Gerente",      "Todo lo operativo, incluidas cancelaciones."],
  ["accountant", "Contabilidad", "Timbra y cancela CFDI, registra pagos."],
  ["owner",      "Propietario",  "Todo, incluida la zona de peligro."],
];

export function Vendedores({ membresia }) {
  const [copiado, setCopiado] = useState("");
  const tenantId = membresia?.tenant_id;
  const puedeAdministrar = puedeMembresia(membresia, "asignar_cartera");

  const [gente, setGente] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  // El código recién generado. Vive solo en memoria y solo hasta que se cierra.
  const [nuevo, setNuevo] = useState(null);
  const [form, setForm] = useState({ email: "", rol: "agent", esquema: "comision", dias: 7 });
  const [ocupado, setOcupado] = useState(false);
  const [cartera, setCartera] = useState(false);
  const [pagando, setPagando] = useState(null);

  const recargar = useCallback(async () => {
    if (!tenantId || !puedeAdministrar) { setCargando(false); return; }
    setCargando(true); setError("");
    try {
      const [e, i] = await Promise.all([equipo(tenantId), invitacionesPendientes(tenantId)]);
      setGente(e || []); setTickets(i || []);
    } catch (err) {
      setError(err.message);
    }
    setCargando(false);
  }, [tenantId, puedeAdministrar]);

  useEffect(() => { recargar(); }, [recargar]);

  if (!puedeAdministrar) {
    return (
      <div>
        <SectionTitle sub="Administración del equipo">Vendedores</SectionTitle>
        <Card>
          <p style={{ color: "#888", fontSize: 13 }}>
            {motivoBloqueo(membresia?.role, "asignar_cartera")}
          </p>
        </Card>
      </div>
    );
  }

  const invitar = async () => {
    setOcupado(true); setError("");
    try {
      const r = await crearInvitacion(tenantId, form);
      setNuevo({ ...r, ...form });
      setForm({ email: "", rol: "agent", esquema: "comision", dias: 7 });
      await recargar();
    } catch (err) { setError(err.message); }
    setOcupado(false);
  };

  const activos = gente.filter(g => g.is_active);
  const inactivos = gente.filter(g => !g.is_active);
  const pendientesDeTasa = activos.reduce((t, g) => t + (g.sin_tasa || 0), 0);

  return (
    <div>
      <SectionTitle sub={`${activos.length} personas · ${tickets.length} ticket${tickets.length !== 1 ? "s" : ""} sin usar`}>
        Vendedores
      </SectionTitle>

      {/* Lo que el equipo está pidiendo va ARRIBA de lo que el equipo es: una
          solicitud sin responder detiene una venta, la cartera puede esperar.
          Se dibuja sola cuando no hay nada que decidir. */}
      <AutorizarPrecios membresia={membresia} />

      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220, fontSize: 12, color: "#888", lineHeight: 1.6 }}>
            Reparte clientes entre el equipo y fija la comisión de cada cuenta.
            {pendientesDeTasa > 0 && (
              <> <span style={{ color: "#c9a96e" }}>
                Hay {pendientesDeTasa} asignada{pendientesDeTasa !== 1 ? "s" : ""} sin tasa.
              </span></>
            )}
          </div>
          <button onClick={() => setCartera(true)}
            style={{ ...btnSecondary, padding: "9px 18px", fontSize: 12,
                     borderColor: "#2a2a3a", color: "#a0a0c9" }}>
            📇 Repartir cartera
          </button>
        </div>
      </Card>

      {cartera && (
        <CarteraPanel tenantId={tenantId} gente={gente}
          onCerrar={() => setCartera(false)} onCambio={recargar} />
      )}

      {pagando && (
        <PagoComision tenantId={tenantId} persona={pagando}
          onCerrar={() => setPagando(null)}
          onListo={(r) => {
            setPagando(null);
            recargar();
            if (r?.aviso) alert("⚠️ " + r.aviso);
          }} />
      )}

      {error && (
        <Card style={{ borderColor: "#3a1a1a" }}>
          <div style={{ color: "#f87171", fontSize: 13 }}>⚠️ {error}</div>
        </Card>
      )}

      {/* ── El código recién creado ────────────────────────────────────── */}
      {nuevo && (() => {
        const liga = ligaDeTicket(nuevo.codigo);
        const mensaje = mensajeDeInvitacion(nuevo.codigo,
          { vinicola: membresia?.tenant?.name, expira: nuevo.expira });
        const copiar = (texto, cual) => {
          navigator.clipboard?.writeText(texto);
          setCopiado(cual);
          setTimeout(() => setCopiado(""), 2000);
        };
        return (
          <Card style={{ borderColor: "#2a3a1a", background: "#101408" }}>
            <div style={{ fontSize: 11, color: "#a8d060", letterSpacing: 2, marginBottom: 10 }}>
              🎫 TICKET GENERADO
            </div>

            {/* ── El QR ────────────────────────────────────────────────────
                El operador: "el dueño o administrador pueden generar un usuario
                y firmar al usuario con QR". Es la MISMA liga de abajo, dibujada
                para que se apunte con la cámara — que es lo que hace posible
                dar de alta a alguien en una máquina que no es la tuya, sin
                dictar un código de diez caracteres ni mandar un mensaje.

                Fondo blanco y margen a propósito: un QR sobre el fondo oscuro
                de la app, o pegado al borde, es un QR que a veces no se lee. */}
            <div style={{ display: "flex", justifyContent: "center", margin: "14px 0 6px" }}>
              <img src={qrComoImagen(liga, { modulo: 6, margen: 4 })}
                alt={`Código QR de la liga de invitación ${nuevo.codigo}`}
                style={{ width: 220, height: 220, borderRadius: 8,
                         imageRendering: "pixelated" }} />
            </div>
            <div style={{ fontSize: 11, color: "#7a8a5a", textAlign: "center",
                          marginBottom: 6 }}>
              Que lo apunte con la cámara de su teléfono
            </div>

            {/* ── El código, abajo y chiquito ───────────────────────────────
                Estaba arriba, en 26 píxeles y centrado — o sea, presentado como
                LA cosa que hay que hacer. El operador reportó lo que produce
                eso: "se confunde con el código, la contraseña y el correo".

                El código no es una tercera vía: es lo mismo que el QR y la liga,
                escrito de otra forma. Enseñarlo como protagonista invita a
                dictarlo, y dictarlo es el camino más largo de los tres. Se queda
                porque a veces la liga no llega, pero como respaldo. */}
            <div style={{ fontSize: 10, color: "#7a8a5a", letterSpacing: 1, marginTop: 8 }}>
              LIGA PARA MANDARLE
            </div>
            <div style={{ fontFamily: "monospace", fontSize: 11, color: "#8aa070",
                          background: "#0a0a0a", border: "1px solid #1a2a10", borderRadius: 6,
                          padding: "8px 10px", marginTop: 4, wordBreak: "break-all",
                          userSelect: "all" }}>
              {liga}
            </div>

            {/* El código, como respaldo y dicho como tal. */}
            <details style={{ marginTop: 10 }}>
              <summary style={{ fontSize: 11, color: "#666", cursor: "pointer" }}>
                Si la liga no le llega, dictarle el código
              </summary>
              <div style={{ fontFamily: "monospace", fontSize: 18, color: "#8aa070",
                            letterSpacing: 2, padding: "8px 0", userSelect: "all" }}>
                {nuevo.codigo}
              </div>
            </details>

            <div style={{ fontSize: 12, color: "#7a8a5a", lineHeight: 1.7, margin: "12px 0" }}>
              {nuevo.email
                ? <>Solo lo puede usar <b>{nuevo.email}</b>, y con ese correo tendrá que
                    entrar — para el alta sin correo, genera el ticket sin llenar ese campo.</>
                : <>Sirve para <b>cualquiera</b> que lo reciba. Entra sin correo ni
                    contraseña: abre la liga y le sale un botón.</>}
              {" "}Una sola vez, vence el {new Date(nuevo.expira).toLocaleDateString("es-MX")}.
              <br />
              <span style={{ color: "#666" }}>
                Cópialo ahora: al cerrar esto no se vuelve a mostrar.
              </span>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={() => copiar(mensaje, "msg")}
                style={{ ...btnPrimary, padding: "9px 16px", fontSize: 12 }}>
                {copiado === "msg" ? "✓ Copiado" : "💬 Copiar mensaje completo"}
              </button>
              <button onClick={() => copiar(liga, "liga")}
                style={{ ...btnSecondary, padding: "9px 16px", fontSize: 12,
                         borderColor: "#3a5a1a", color: "#a8d060" }}>
                {copiado === "liga" ? "✓ Copiado" : "🔗 Solo la liga"}
              </button>
              <a href={`https://wa.me/?text=${encodeURIComponent(mensaje)}`}
                target="_blank" rel="noopener"
                style={{ ...btnSecondary, padding: "9px 16px", fontSize: 12,
                         textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
                WhatsApp
              </a>
              <button onClick={() => setNuevo(null)}
                style={{ ...btnSecondary, marginLeft: "auto", padding: "9px 16px", fontSize: 12 }}>
                Cerrar
              </button>
            </div>
          </Card>
        );
      })()}

      {/* ── Invitar ────────────────────────────────────────────────────── */}
      <Card>
        <div style={{ fontSize: 11, color: "#70c9c9", letterSpacing: 2, marginBottom: 12 }}>
          ＋ INVITAR A ALGUIEN
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                      gap: 12, marginBottom: 12 }}>
          <div>
            <Label>Correo (opcional)</Label>
            <input type="email" value={form.email} placeholder="vendedor@correo.com"
              onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <Label>Rol</Label>
            <select value={form.rol} onChange={e => setForm({ ...form, rol: e.target.value })}
              style={selectStyle}>
              {ROLES.map(([v, etq]) => <option key={v} value={v}>{etq}</option>)}
            </select>
          </div>
          <div>
            <Label>Cómo se le paga</Label>
            <select value={form.esquema} onChange={e => setForm({ ...form, esquema: e.target.value })}
              style={selectStyle}>
              <option value="comision">Por comisión</option>
              <option value="salario">Sueldo fijo</option>
            </select>
          </div>
          <div>
            <Label>Vence en (días)</Label>
            <input type="number" min="1" max="30" value={form.dias}
              onChange={e => setForm({ ...form, dias: Math.min(30, Math.max(1, +e.target.value || 7)) })}
              style={inputStyle} />
          </div>
        </div>

        <div style={{ fontSize: 11, color: "#666", lineHeight: 1.7, marginBottom: 12 }}>
          {ROLES.find(r => r[0] === form.rol)?.[2]}
          <br />
          {form.esquema === "comision"
            ? "La comisión NO se fija aquí: se pone cliente por cliente al asignarle cartera, porque cubre también el reparto."
            : "Sueldo fijo: sus ventas no generan comisión, aunque los clientes tengan tasa."}
          {!form.email && (
            <><br /><span style={{ color: "#8aa070" }}>
              Sin correo el ticket sirve para cualquiera que lo reciba — es lo que
              quieres si solo tienes su teléfono. Con correo queda amarrado a esa cuenta.
            </span></>
          )}
        </div>

        <button onClick={invitar} disabled={ocupado}
          style={{ ...btnPrimary, padding: "10px 20px", fontSize: 13, opacity: ocupado ? 0.5 : 1 }}>
          {ocupado ? "⏳ Generando…" : "🎫 Generar ticket"}
        </button>
      </Card>

      {/* ── Tickets sin usar ───────────────────────────────────────────── */}
      {tickets.length > 0 && (
        <Card>
          <div style={{ fontSize: 11, color: "#70c9c9", letterSpacing: 2, marginBottom: 12 }}>
            🎫 SIN USAR
          </div>
          {tickets.map(t => (
            <div key={t.code} style={{ display: "flex", alignItems: "center", gap: 10,
                                       padding: "8px 0", borderBottom: "1px solid #1a1a1a",
                                       flexWrap: "wrap", opacity: t.vencido ? 0.5 : 1 }}>
              <span style={{ fontFamily: "monospace", fontSize: 14, color: "#c9a96e",
                             letterSpacing: 1 }}>{t.code}</span>
              <span style={{ fontSize: 12, color: "#888" }}>{t.email || "al portador"}</span>
              <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4,
                             background: "#1a1a1a", color: "#888" }}>
                {ROLES.find(r => r[0] === t.role)?.[1] || t.role}
              </span>
              <span style={{ fontSize: 11, color: t.vencido ? "#f87171" : "#666" }}>
                {t.vencido ? "vencido" : `vence ${new Date(t.expires_at).toLocaleDateString("es-MX")}`}
              </span>
              <button onClick={async () => {
                  await revocarInvitacion(tenantId, t.code); recargar();
                }}
                style={{ ...btnSecondary, marginLeft: "auto", padding: "4px 10px",
                         fontSize: 11, borderColor: "#3a1a1a", color: "#c97070" }}>
                Cancelar
              </button>
            </div>
          ))}
        </Card>
      )}

      {/* ── El equipo ──────────────────────────────────────────────────── */}
      <Card>
        <div style={{ fontSize: 11, color: "#70c9c9", letterSpacing: 2, marginBottom: 12 }}>
          👥 EQUIPO
        </div>

        {pendientesDeTasa > 0 && (
          <div style={{ padding: "8px 12px", background: "#1f1a0d", border: "1px solid #3a3a1a",
                        borderRadius: 6, fontSize: 12, color: "#c9a96e", marginBottom: 12 }}>
            {pendientesDeTasa} cuenta{pendientesDeTasa !== 1 ? "s" : ""} asignada{pendientesDeTasa !== 1 ? "s" : ""} sin
            comisión definida. No generan pago hasta que se les ponga tasa, cliente por cliente.
          </div>
        )}

        {cargando ? (
          <div style={{ color: "#555", fontSize: 13 }}>Cargando…</div>
        ) : (
          [...activos, ...inactivos].map(g => (
            <div key={g.membership_id}
              style={{ padding: "12px 0", borderBottom: "1px solid #1a1a1a",
                       opacity: g.is_active ? 1 : 0.45 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  {/* El operador: "saber qué vendedor es cuál". Quien entra por
                      ticket no tiene correo de verdad —lleva uno interno bajo
                      `@dispositivos.invalid`— así que enseñarlo era enseñar
                      ruido. Si todavía no ha puesto su nombre se dice eso, con
                      todas sus letras, en vez de un identificador que no le
                      sirve a nadie. */}
                  <div style={{ fontSize: 14, color: "#e8e0d4" }}>
                    {g.nombre || (esDispositivo(g.email)
                      ? <span style={{ color: "#8a8266" }}>Sin nombre todavía</span>
                      : g.email)}
                    {!g.is_active && <span style={{ fontSize: 11, color: "#c97070" }}> · dado de baja</span>}
                  </div>
                  <div style={{ fontSize: 11, color: "#666" }}>
                    {esDispositivo(g.email) ? "entró con ticket · sin correo" : g.email}
                  </div>
                </div>

                <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4,
                               background: "#1a1a1a", color: "#c9a96e" }}>
                  {etiquetaRol(g.role)}
                </span>

                {g.scheme && (
                  <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4,
                                 background: "#1a1a1a", color: "#888" }}>
                    {g.scheme === "comision" ? "por comisión" : "sueldo fijo"}
                  </span>
                )}

                <span title={`${g.clientes} clientes en su cartera`}
                  style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4,
                           background: g.sin_tasa > 0 ? "#1f1a0d" : "#111",
                           color: g.sin_tasa > 0 ? "#c9a96e" : "#666" }}>
                  {g.clientes} client{g.clientes === 1 ? "e" : "es"}
                  {g.sin_tasa > 0 ? ` · ${g.sin_tasa} sin tasa` : ""}
                </span>

                {g.scheme === "comision" && Number(g.saldo) > 0 && (
                  <span title="El cliente ya pagó; falta depositarle"
                    style={{ fontSize: 11, padding: "3px 9px", borderRadius: 4,
                             background: "#12140d", border: "1px solid #2a3a1a",
                             color: "#a8d060", fontWeight: 600 }}>
                    por acreditarse {fmt(Number(g.saldo))}
                  </span>
                )}
                {g.scheme === "comision" && Number(g.por_cobrar) > 0 && (
                  <span title="La venta ya es suya; el cliente aún no le paga a la vinícola"
                    style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4,
                             background: "#111", color: "#8a8a6a" }}>
                    acreditada {fmt(Number(g.por_cobrar))}
                  </span>
                )}

                {/* Sin CLABE no se le puede depositar. Vale la pena verlo antes
                    de que llegue el día de pagar. */}
                {g.scheme === "comision" && !g.tiene_clabe && (
                  <span title="No ha registrado su CLABE"
                    style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4,
                             background: "#1f1a0d", color: "#facc15" }}>
                    sin CLABE
                  </span>
                )}
              </div>

              {g.is_active && (
                <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                  {/* «Ver como» VIVÍA AQUÍ y se fue a Plataforma (0.44.0).
                      El operador: "es una herramienta de desarrollo, no es una
                      herramienta de administración... la vista está mezclada."

                      Y estaba mezclada de verdad: una acción de plataforma
                      dibujada dentro de la pantalla que administra UNA vinícola.
                      Aunque la base ya la negaba a cualquiera que no fuera
                      superusuario, tenerla aquí decía que era parte de
                      administrar un equipo. No lo es. */}
                  {g.scheme === "comision" && (
                    <button onClick={() => setPagando(g)}
                      style={{ ...btnSecondary, padding: "4px 12px", fontSize: 11,
                               borderColor: Number(g.saldo) > 0 ? "#3a5a1a" : "#222",
                               color: Number(g.saldo) > 0 ? "#a8d060" : "#888" }}>
                      💵 Registrar pago de comisión
                    </button>
                  )}
                  <button onClick={async () => {
                      const nuevoEsq = g.scheme === "comision" ? "salario" : "comision";
                      const etq = nuevoEsq === "comision" ? "por comisión" : "sueldo fijo";
                      if (!confirm(`Cambiar a ${etq}.\n\nEl periodo actual se cierra hoy y se abre uno nuevo: ` +
                                   `lo vendido hasta ahora se liquida con el esquema anterior.`)) return;
                      try { await fijarEsquema(tenantId, g.membership_id, nuevoEsq); recargar(); }
                      catch (e) { setError(e.message); }
                    }}
                    style={{ ...btnSecondary, padding: "4px 12px", fontSize: 11 }}>
                    Cambiar a {g.scheme === "comision" ? "sueldo" : "comisión"}
                  </button>

                  <button onClick={async () => {
                      const motivo = prompt(
                        `Dar de baja a ${g.nombre || g.email}.\n\n` +
                        `No se borra nada: pierde el acceso y se libera su asiento. ` +
                        `Sus ${g.clientes} cliente(s) NO se reparten solos — quedan para que ` +
                        `decidas a quién pasan.\n\nMotivo:`);
                      if (motivo === null) return;
                      try {
                        const r = await darDeBaja(tenantId, g.membership_id, motivo);
                        if (r?.clientes_por_reasignar > 0)
                          alert(`Dado de baja. Quedan ${r.clientes_por_reasignar} cliente(s) por reasignar.`);
                        recargar();
                      } catch (e) { setError(e.message); }
                    }}
                    style={{ ...btnSecondary, padding: "4px 12px", fontSize: 11,
                             borderColor: "#3a1a1a", color: "#c97070" }}>
                    Dar de baja
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </Card>
    </div>
  );
}
